import * as React from 'react';
import { LuArrowRight, LuInfo } from 'react-icons/lu';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { cn } from 'utils/cn';

import { Spinner } from './Spinner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

export interface BaseCardProps {
    className?: string;
    title: string | React.ReactNode;
    titleLink?: string;
    subTitle?: string;
    icon?: React.ElementType;
    headerClassName?: string;
    children?: React.ReactNode;
    loading?: boolean;
    navigateUrl?: string;
    onUpdate?: (newValue: string | number) => void;
    menu?: React.ReactNode;
}

const MaybeWrapWithLink = ({ children, link }: { children: React.ReactNode; link?: string }) => {
    if (link) {
        return <Link to={link}>{children}</Link>;
    }
    return <>{children}</>;
};

export const BaseCard = ({
    className,
    title,
    titleLink,
    subTitle,
    icon,
    headerClassName,
    navigateUrl,
    children,
    loading = false,
    menu,
}: BaseCardProps) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const Icon = icon || LuInfo;
    const IconComponent = () => {
        if (icon) {
            return <Icon className="h-5 w-5 text-black" />;
        }
        return <></>;
    };

    const headerCn = cn('group', 'bg-gray-300', headerClassName);
    return (
        <Card className={cn(className, 'overflow-hidden rounded-t-xl')}>
            <CardHeader className={headerCn}>
                <div className="flex items-center gap-2">
                    <IconComponent />
                    <MaybeWrapWithLink link={titleLink}>
                        <CardTitle className="text-lg font-semibold text-black">
                            <div className="flex items-center gap-2">
                                {title}
                                {titleLink && (
                                    <LuArrowRight className="opacity-0 transition-opacity duration-200 group-hover:animate-pulse group-hover:opacity-100" />
                                )}
                            </div>
                            {subTitle && <div className="text-sm font-normal text-gray-700">{subTitle}</div>}
                        </CardTitle>
                    </MaybeWrapWithLink>

                    <div className="ml-auto flex items-center gap-2">
                        {menu && <div>{menu}</div>}
                        {navigateUrl && (
                            <div
                                className="cursor-pointer opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                                onClick={() => navigate(pathname.split('/').slice(0, -1).join('/') + '/' + navigateUrl)}
                            >
                                <LuArrowRight />
                            </div>
                        )}
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="py-8">
                        <Spinner showText />
                    </div>
                ) : (
                    children
                )}
            </CardContent>
        </Card>
    );
};

export const BaseRow = ({ children, label }: { children: React.ReactNode; label?: string }) => {
    return (
        <div className="flex items-center">
            {label && <span className="w-40 text-sm font-medium text-gray-700">{label}:</span>}
            <div className="flex flex-1 items-center gap-2">{children}</div>
        </div>
    );
};
